import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import {classes, style, vars} from './text-area.st.css';
import {assertNotNullable, HtmlTextDirection, OmitStrict} from '@wix/devzai-utils-common';
import {useControlledProperty} from "../hooks";
import {ScrollView} from "../scroll-view/scroll-view";


export const TextArea = React.memo(React.forwardRef(function TextArea(props: TextArea.Props, ref: React.Ref<TextArea>) {

    const {
        onTextChange,
        value,
        defaultValue,
        textDirection = HtmlTextDirection.Auto,

        inputId,
        minLines = 2,
        maxLines,
        lineHeight,

        placeholder,
        name,
        readOnly,
        disabled,
        autoComplete,

        className,
        style: styleAttributes,
        ...htmlAttributes
    } = props;

    const [inFocus, setInFocus] = useState(false);

    const inputRef = useRef<HTMLTextAreaElement>(null);

    const hasMaxLines = maxLines !== undefined;

    const [currentValue, setCurrentValue] = useControlledProperty(value, defaultValue, onTextChange, '');

    const textArea = useMemo(() => {
        return {
            getInputElement() {
                return assertNotNullable(inputRef.current);
            },

            focus() {
                inputRef.current?.focus()
            }
        }
    }, []);

    useImperativeHandle(ref, () => textArea, [textArea]);

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, {
                inFocus: inFocus,
                hasMaxLines: hasMaxLines
            }, props.className)}
            style={{
                ...styleAttributes,
                [vars.minLines]: minLines,
                [vars.maxLines]: Math.max(maxLines ?? 0, minLines),
                [vars.lineHeight]: typeof lineHeight === 'number' ? `${lineHeight}px` : lineHeight,
            }}
        >
            <ScrollView
                className={style(classes.textAreaScrollView, {})}
            >
                <div
                    className={style(classes.textAreaContent, classes.verticalPaddingTarget, classes.horizontalPaddingTarget)}
                >
                    <textarea
                        id={inputId}
                        value={currentValue}
                        placeholder={placeholder}
                        name={name}
                        readOnly={readOnly}
                        disabled={disabled}
                        autoComplete={autoComplete}
                        className={style(classes.textarea, {})}
                        dir={textDirection}
                        ref={inputRef}
                        onChange={event => {
                            const inputElement = event.target as HTMLTextAreaElement;

                            setCurrentValue(inputElement.value)
                        }}
                        onFocus={() => {
                            setInFocus(true);
                        }}
                        onBlur={() => {
                            setInFocus(false);
                        }}
                    />
                    <div className={style(classes.valueReplication, {})} aria-hidden={'true'}>{`${currentValue} `}</div>
                </div>
            </ScrollView>
        </div>
    );
}));

export namespace TextArea {

    export interface Props extends
        OmitStrict<React.HTMLAttributes<any>, 'onChange'>,
        Pick<React.InputHTMLAttributes<HTMLTextAreaElement>, 'placeholder' | 'name' | 'readOnly' | 'disabled' | 'autoComplete'>
    {
        value?: string;
        defaultValue?: string;
        onTextChange?: (value: string) => void;
        minLines?: number;
        maxLines?: number;
        inputId?: string;
        textDirection?: HtmlTextDirection;

        lineHeight?: string | number;
    }
}

export interface TextArea {
    focus(): void;

    getInputElement () : HTMLTextAreaElement;
}